import { useEffect, useRef, useState } from 'react';
import { Camera, X, Loader } from 'lucide-react';
import { VIDEO_CONSTRAINTS } from '../../utils/constants';

const PHOTO_COUNTDOWN_SECONDS = 8;

function PhotoCaptureScreen({ event, guestInfo, onComplete, onCancel }) {
  const [isInitializing, setIsInitializing] = useState(false);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [countdownValue, setCountdownValue] = useState(null);
  const [error, setError] = useState(null);
  const [isCameraReady, setIsCameraReady] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const audioContextRef = useRef(null);

  useEffect(() => {
    initializeCamera();

    return () => {
      cleanup();
    };
  }, []);

  const playTone = async (frequency, duration = 130, volume = 0.18) => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;

      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContextClass();
      }

      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }

      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
      gainNode.gain.setValueAtTime(volume, ctx.currentTime);

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.start();
      oscillator.stop(ctx.currentTime + duration / 1000);
    } catch (audioError) {
      // Silent fallback when sound output is blocked by browser policies.
      console.warn('Countdown sound unavailable:', audioError);
    }
  };

  const initializeCamera = async () => {
    try {
      setIsInitializing(true);
      setError(null);
      setIsCameraReady(false);

      // Reset existing stream before re-initializing to avoid browser camera-lock issues.
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: VIDEO_CONSTRAINTS.video,
        audio: false
      });

      streamRef.current = stream;
      setIsCameraReady(true);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch((playError) => {
          // Stream is available; playback can still fail transiently on some browsers.
          console.warn('Photo preview autoplay failed:', playError);
        });
      }
    } catch (err) {
      console.error('Photo camera initialization error:', err);
      setIsCameraReady(false);
      if (err?.name === 'NotAllowedError') {
        setError('Camera permission denied. Please allow camera access.');
      } else if (err?.name === 'NotFoundError') {
        setError('No camera found on this device.');
      } else {
        setError('Unable to access camera. Please check permissions.');
      }
    } finally {
      setIsInitializing(false);
    }
  };

  const cleanup = () => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsCameraReady(false);

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video || video.videoWidth <= 0 || video.videoHeight <= 0) {
      setError('Camera preview is not ready. Please try again.');
      setIsCountingDown(false);
      setCountdownValue(null);
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setError('Failed to capture photo. Please try again.');
      setIsCountingDown(false);
      setCountdownValue(null);
      return;
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setError('Failed to capture photo. Please try again.');
          setIsCountingDown(false);
          setCountdownValue(null);
          return;
        }

        playTone(1200, 220, 0.24);
        setIsCountingDown(false);
        setCountdownValue(null);
        onComplete(blob);
      },
      'image/jpeg',
      0.92
    );
  };

  const startCountdown = async () => {
    if (isCountingDown || isInitializing || !!error) return;

    setIsCountingDown(true);
    setCountdownValue(PHOTO_COUNTDOWN_SECONDS);
    await playTone(880, 120, 0.16);

    countdownTimerRef.current = setInterval(async () => {
      setCountdownValue((prev) => {
        if (prev === null) return prev;
        if (prev <= 1) {
          if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
          }
          capturePhoto();
          return 0;
        }

        playTone(880, 120, 0.16);
        return prev - 1;
      });
    }, 1000);
  };

  const handleCancel = () => {
    cleanup();
    onCancel();
  };

  return (
    <div className="fullscreen flex flex-col bg-black">
      <div className="absolute top-0 left-0 right-0 z-10 p-6 bg-gradient-to-b from-black/80 to-transparent">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white mb-1">
              {isCountingDown ? 'Get Ready...' : 'Ready for Photo'}
            </h2>
            <p className="text-neutral-grey-300">
              {guestInfo.name || 'Anonymous'} • {event.eventName}
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-all"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>

      <div className="flex-1 relative z-0">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          webkit-playsinline="true"
          x-webkit-airplay="allow"
          className="absolute inset-0 w-full h-full object-cover transform scale-x-[-1] z-0"
        />

        {isInitializing && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-20">
            <div className="text-center">
              <Loader className="w-10 h-10 text-gold animate-spin mx-auto mb-4" />
              <p className="text-white text-lg">Starting camera...</p>
            </div>
          </div>
        )}

        {error && !isCameraReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-20">
            <div className="text-center">
              <div className="w-20 h-20 bg-crimson/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-10 h-10 text-crimson" />
              </div>
              <p className="text-crimson text-lg mb-4">{error}</p>
              <button
                onClick={initializeCamera}
                className="px-6 py-3 bg-gold hover:bg-gold-light text-dark-slate font-semibold rounded-lg transition-all"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {isCountingDown && countdownValue !== null && countdownValue > 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 z-20">
            <div className="w-36 h-36 rounded-full bg-black/60 border-2 border-gold/80 flex items-center justify-center shadow-2xl">
              <span className="text-6xl font-bold text-gold">{countdownValue}</span>
            </div>
          </div>
        )}

        <div className="absolute bottom-6 right-6 z-10 opacity-60">
          <div className="flex items-center gap-2 bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/10">
            <Camera className="w-4 h-4 text-gold" />
            <span className="text-white text-sm font-semibold tracking-wide">MomentBox</span>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 z-20 p-8 bg-gradient-to-t from-black/80 to-transparent">
        <div className="flex justify-center">
          <button
            onClick={startCountdown}
            disabled={isInitializing || (!isCameraReady && !!error) || isCountingDown}
            className="w-24 h-24 bg-gold hover:bg-gold-light rounded-full flex items-center justify-center transition-all shadow-2xl shadow-gold/50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Camera className="w-12 h-12 text-dark-slate" />
          </button>
        </div>

        <p className="text-center text-neutral-grey-300 mt-6 text-lg">
          {isCountingDown
            ? `Photo in ${countdownValue || 0} second${countdownValue === 1 ? '' : 's'}`
            : 'Tap the gold shutter button to start an 8-second countdown'}
        </p>
      </div>
    </div>
  );
}

export default PhotoCaptureScreen;
